/**
 * Converts a hex color string to rgb()/rgba() format.
 *
 * Browsers normalize inline style hex colors to rgb() when parsing HTML,
 * which causes React hydration mismatches. Using rgb() on the server
 * ensures the rendered HTML matches what the browser produces.
 *
 * Supports #rgb, #rrggbb, #rgba, and #rrggbbaa formats.
 */
export const hexToRgb = (hex: string | undefined): string | undefined => {
  if (!hex) return undefined

  const cleaned = hex.replace("#", "")

  // Expand shorthand (#abc → aabbcc, #abcd → aabbccdd)
  const full =
    cleaned.length === 3 || cleaned.length === 4
      ? cleaned
          .split("")
          .map((c) => c + c)
          .join("")
      : cleaned

  if (full.length === 8) {
    const num = parseInt(full, 16)
    if (isNaN(num)) return hex
    const r = (num >> 24) & 255
    const g = (num >> 16) & 255
    const b = (num >> 8) & 255
    const a = num & 255
    return `rgba(${r}, ${g}, ${b}, ${+(a / 255).toFixed(3)})`
  }

  if (full.length === 6) {
    const num = parseInt(full, 16)
    if (isNaN(num)) return hex
    const r = (num >> 16) & 255
    const g = (num >> 8) & 255
    const b = num & 255
    return `rgb(${r}, ${g}, ${b})`
  }

  return hex
}
